import React, { useEffect, useState } from 'react'
import { Accordion, AccordionDetails, SxProps, Theme } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import AccordionHeader from './AccordionHeader'

interface StyleProps {
  elevation: number
  depth?: number
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  container: {
    borderWidth: 1,
    borderColor:
      props.depth && props.depth > 0 ? theme.palette.shades.shade03 : props.elevation === 0 ? 'transparent' : theme.palette.accordion.border,
    backgroundColor: props.depth
      ? props.depth % 2 === 0
        ? theme.palette.background.paper
        : theme.palette.shades.shade04
      : theme.palette.background.paper,
    borderStyle: 'solid',
    paddingBottom: 0,
    boxShadow: props.elevation === 0 ? 'none' : undefined,
    '&:before': {
      ...(props.elevation === 0 ? { backgroundColor: 'transparent' } : { background: 'none' }),
    },
  },
}))

export interface AccordionItemProps extends React.RefAttributes<HTMLDivElement> {
  title: string | React.ReactElement
  headerChildren?: React.ReactNode
  headerIcon?: React.ReactNode
  className?: string
  sx?: SxProps<Theme>
  accordionDetailsSx?: SxProps<Theme>
  expanded?: boolean
  hideIcon?: boolean
  elevation?: number
  callback?: (expanded?: boolean) => void
  expandOnlyOnIcon?: boolean
  depth?: number
  headerContentClassName?: string
  contentClassName?: string
  children?: React.ReactNode
  disabled?: boolean
  onClick?: () => void
  applyDefaultHeaderChildClasses?: boolean
  customWidth?: string | number
}

const AccordionItem: React.FC<AccordionItemProps> = React.forwardRef<HTMLDivElement, AccordionItemProps>(
  (
    {
      title,
      children,
      headerChildren,
      headerIcon,
      className,
      sx,
      accordionDetailsSx,
      expanded,
      callback,
      headerContentClassName,
      contentClassName,
      hideIcon = false,
      elevation = 1,
      expandOnlyOnIcon = false,
      depth = 0,
      disabled = false,
      onClick,
      applyDefaultHeaderChildClasses = true,
      customWidth,
    },
    ref
  ) => {
    const { classes, cx } = useStyles({ elevation, depth })
    const [isExpanded, setIsExpanded] = useState(expanded)

    useEffect(() => {
      if (expanded !== undefined) {
        setIsExpanded(expanded)
      }
    }, [expanded])

    return (
      <Accordion
        disableGutters
        ref={ref}
        disabled={disabled}
        className={cx(classes.container, className)}
        sx={sx}
        elevation={elevation}
        expanded={isExpanded ?? false}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onChange={(event: React.ChangeEvent<Record<string, any>>, expanded: boolean) => {
          if (expandOnlyOnIcon) return
          setIsExpanded(expanded)
          callback?.(expanded)
        }}
        data-qa={`accordion-item-${title}`}
        slotProps={{ transition: { unmountOnExit: true } }}
        onClick={onClick}
      >
        <AccordionHeader
          title={title}
          hideIcon={hideIcon}
          expandOnlyOnIcon={expandOnlyOnIcon}
          onClick={() => {
            setIsExpanded(!isExpanded)
            callback?.(expanded)
          }}
          isExpanded={isExpanded}
          headerContentClassName={headerContentClassName}
          icon={headerIcon}
          applyDefaultChildClasses={applyDefaultHeaderChildClasses}
          customWidth={customWidth}
        >
          {headerChildren}
        </AccordionHeader>
        <AccordionDetails className={contentClassName} sx={accordionDetailsSx}>
          {children}
        </AccordionDetails>
      </Accordion>
    )
  }
)

AccordionItem.displayName = 'AccordionItem'

export default AccordionItem
